/**
 * S3 Source (`s3.source`) inspector: pick an S3 connection, a bucket, and an
 * object, then Test to preview the parsed rows inline.
 *
 * The Blob Source form with S3 vocabulary, including the auto-preview that
 * populates downstream column pickers without a manual Test. Test is a
 * read-only preview that runs the *same* parser a run does, so what you see is
 * what you get.
 *
 * The preview is rendered by a component the canvas passes in (`renderPreview`)
 * rather than imported from `PipelineCanvas.tsx`: the canvas already imports
 * this file, and importing it back would make the two modules circular.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Select, Stack, Text, TextInput } from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { s3ReadTable, type ReadTableResult } from "@/shared/api/integration-browse";
import { toastApiError } from "@/shared/ui/toast";

import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";
import { useS3Browse } from "./useS3Browse";

/** Common separators + auto. Tab's value is a real tab character. */
const DELIMITER_OPTIONS = [
  { value: "", label: "Auto-detect" },
  { value: ",", label: "Comma  ," },
  { value: ";", label: "Semicolon  ;" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe  |" },
];

/** Union of column names across rows, first-seen order. */
function columnsOf(rows: Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  const cols: string[] = [];
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (!seen.has(k)) {
        seen.add(k);
        cols.push(k);
      }
    }
  }
  return cols;
}

export function S3SourceConfig({
  config,
  editable,
  onChange,
  onColumns,
  renderPreview,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onColumns?: (cols: string[]) => void;
  /** How to render the previewed rows — the canvas passes its `DataView`. */
  renderPreview: (rows: Record<string, unknown>[]) => ReactNode;
}) {
  const getToken = useAccessToken();
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const bucket = String(config.bucket ?? "");
  const path = String(config.path ?? "");
  const sheet = String(config.sheet ?? "");
  const delimiter = String(config.delimiter ?? "");
  const isExcel = /\.xlsx$/i.test(path);
  const isDelimited = /\.(csv|tsv)$/i.test(path);

  // Objects are listed from the bucket root (flat) — a searchable Select
  // handles many, same as the Blob source.
  const { connections, buckets, objects } = useS3Browse(connectionId, bucket, null);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState<ReadTableResult | null>(null);

  // Signature of everything that affects the parsed columns. When it changes,
  // the cached columns are stale and we re-load.
  const previewKey = [
    connectionId,
    bucket,
    path,
    isExcel ? sheet : "",
    isDelimited ? delimiter : "",
  ].join("|");
  const savedKey = String(config.previewKey ?? "");
  const attemptedKey = useRef("");

  const loadPreview = useCallback(
    async (silent: boolean) => {
      if (!connectionId || !bucket || !path) return;
      setTesting(true);
      try {
        const token = await getToken();
        const result = await s3ReadTable(token, connectionId, bucket, path, {
          sheet: isExcel ? sheet : null,
          delimiter: isDelimited ? delimiter : null,
        });
        const cols = columnsOf(result.rows);
        setPreview(result);
        onColumns?.(cols); // immediate (this session)
        // Persist columns + the config they came from → survives reload and
        // feeds downstream pickers without re-fetching.
        onChange("previewColumns", cols);
        onChange("previewKey", previewKey);
      } catch (err) {
        if (!silent) toastApiError("Preview failed", err);
      } finally {
        setTesting(false);
      }
    },
    [
      getToken,
      connectionId,
      bucket,
      path,
      isExcel,
      sheet,
      isDelimited,
      delimiter,
      onChange,
      onColumns,
      previewKey,
    ],
  );

  // Auto-load columns once the source is fully configured (and again if the
  // object / sheet / delimiter changes), so downstream column pickers populate
  // without a manual Test. Silent on error — the Test button re-runs it and
  // surfaces the error then.
  useEffect(() => {
    if (!connectionId || !bucket || !path) return;
    if (savedKey === previewKey) return;
    if (attemptedKey.current === previewKey) return;
    attemptedKey.current = previewKey;
    void loadPreview(true);
  }, [connectionId, bucket, path, previewKey, savedKey, loadPreview]);

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="S3 connection"
        placeholder={connections.length ? "Pick a connection" : "No S3 connections set up"}
        data={connections}
        nothingFoundMessage="No S3 connections"
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("bucket", "");
          onChange("path", "");
          setPreview(null);
        }}
      />

      {connectionId && (
        <Select
          label="Bucket"
          placeholder="Pick a bucket"
          data={buckets}
          value={bucket || null}
          disabled={!editable}
          searchable
          onChange={(v) => {
            onChange("bucket", v ?? "");
            onChange("path", "");
            setPreview(null);
          }}
        />
      )}

      {connectionId && bucket && (
        <Select
          label="Object"
          placeholder={objects.length ? "Pick an object" : "No objects in this bucket"}
          description="CSV, TSV, JSON, JSONL, or Excel (.xlsx)"
          data={objects}
          value={path || null}
          disabled={!editable}
          searchable
          nothingFoundMessage="No objects"
          onChange={(v) => {
            onChange("path", v ?? "");
            setPreview(null);
          }}
        />
      )}

      {isExcel && (
        <TextInput
          label="Sheet (optional)"
          placeholder="First sheet"
          value={sheet}
          disabled={!editable}
          onChange={(e) => onChange("sheet", e.currentTarget.value)}
        />
      )}

      {isDelimited && (
        <Select
          label="Delimiter"
          description="How columns are separated. Auto-detect handles most files."
          data={DELIMITER_OPTIONS}
          value={delimiter}
          disabled={!editable}
          allowDeselect={false}
          onChange={(v) => {
            onChange("delimiter", v ?? "");
            setPreview(null);
          }}
        />
      )}

      <Button
        variant="light"
        onClick={() => void loadPreview(false)}
        loading={testing}
        disabled={!connectionId || !bucket || !path}
      >
        Test — preview data
      </Button>

      {preview && (
        <Stack gap={4}>
          {preview.truncated && (
            <Text size="xs" c="yellow.7">
              Large file — showing the first {preview.row_count} rows.
            </Text>
          )}
          {isDelimited && !delimiter && preview.delimiter && (
            <Text size="xs" c="dimmed">
              Detected delimiter: {preview.delimiter === "\t" ? "Tab" : `"${preview.delimiter}"`}
            </Text>
          )}
          {renderPreview(preview.rows)}
        </Stack>
      )}
    </Stack>
  );
}
